import React, { useState, useRef, useEffect } from 'react';
import {
    Autocomplete,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    TextField,
    Chip,
} from '@mui/material';
import FormErrorMessage from '../Errormessage';
import KeyboardArrowDownSharpIcon from '@mui/icons-material/KeyboardArrowDownSharp';
import { styled } from '@mui/system';

const CustomSelect = styled(Select)(({ theme }) => ({
    backgroundColor: '#fff',
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            borderColor: '#ff0000',
        },
        '&:hover fieldset': {
            borderColor: '#000',
            outline: 'none',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#000',
            outline: 'none',
        },
    },
}));

const SelectBox = ({
    label,
    icon: Icon = KeyboardArrowDownSharpIcon,
    className,
    messages,
    error,
    name,
    register,
    placeholder = 'Select',
    list = [],
    value,
    onChange,
    searchBox = false,
    multiple = false,
    disabled,
    ...rest
}) => {
    const [isFocused, setIsFocused] = useState(false);
    const [isLabelShrunk, setIsLabelShrunk] = useState(false);
    const selectRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (selectRef.current && !selectRef.current.contains(event.target)) {
                setIsFocused(false);
                setIsLabelShrunk(value?.length > 0 || (multiple ? value?.length > 0 : value !== ''));
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [value, multiple]);

    useEffect(() => {
        const isActive = (multiple ? value?.length > 0 : value !== '') || isFocused;
        setIsLabelShrunk(isActive);
    }, [isFocused, multiple, value]);

    const handleSelectChange = (event) => {
        const selectedValue = event.target.value;

        if (multiple) {
            const newValue = value.includes(selectedValue)
                ? value.filter((val) => val !== selectedValue)
                : [...value, selectedValue];
            onChange(newValue);
        } else {
            onChange(selectedValue);
        }
    };

    return (
        <FormControl fullWidth ref={selectRef}>
            {label && (
                <InputLabel
                    shrink={isLabelShrunk}
                    sx={{
                        color: '#2E2E2E',
                        backgroundColor: "#FFFFFF",
                        paddingLeft: "2px",
                        paddingRight: "2px",
                        fontFamily: 'Gotham',
                        fontSize: "16px",
                        fontWeight: "400",
                        lineHeight: "26px",
                        '&.Mui-focused': {
                            color: '#2E2E2E',
                        },
                    }}
                >
                    {label}
                </InputLabel>
            )}
            {(multiple || searchBox) ? (
                <Autocomplete
                    multiple={multiple}
                    options={list}
                    getOptionLabel={(option) => option.label || option}
                    filterSelectedOptions={multiple}
                    value={multiple ? list.filter((option) => value?.includes(option.value)) : list.find(option => option.value === value) || null}
                    onChange={(event, newValue) => {
                        const newSelectedValues = multiple
                            ? newValue.map((option) => option.value)
                            : newValue?.value || '';
                        onChange(newSelectedValues);
                    }}
                    onFocus={() => {
                        setIsFocused(true);
                        setIsLabelShrunk(true);
                    }}
                    onBlur={() => setIsFocused(false)}
                    renderInput={(params) => (
                        <TextField {...params} placeholder="Search..." variant="outlined" />
                    )}
                    renderTags={(selected, getTagProps) =>
                        selected.map((option, index) => (
                            <Chip
                                key={option.value}
                                label={option.label || option}
                                {...getTagProps({ index })}
                                onDelete={() => {
                                    const newValue = selected.filter((v) => v.value !== option.value);
                                    onChange(newValue.map((v) => v.value));
                                }}
                            />
                        ))
                    }
                />
            ) : (
                <CustomSelect
                    value={value}
                    disabled={disabled}
                    onChange={handleSelectChange}
                    onFocus={() => {
                        setIsFocused(true);
                        setIsLabelShrunk(true);
                    }}
                    onBlur={() => setIsFocused(false)}
                    name={name}
                    label={label}
                    multiple={multiple}
                    placeholder={placeholder}
                    className={`${className} kg_select_wrapper`}
                    IconComponent={Icon}
                    {...rest}
                    MenuProps={{
                        PaperProps: {
                            style: {
                                maxHeight: '300px',
                            },
                        },
                    }}
                >
                    {list.map((item, index) => (
                        <MenuItem key={index} value={item.value}>
                            {item.label}
                        </MenuItem>
                    ))}
                </CustomSelect>
            )}
            <div>
                <FormErrorMessage error={error} messages={messages} />
            </div>
        </FormControl>
    );
};

export default SelectBox;
